import type { FC } from 'react'
import { useState } from 'react'

import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EmailSendOutlineIcon from 'mdi-react/EmailSendOutlineIcon'
import MessageProcessingOutlineIcon from 'mdi-react/MessageProcessingOutlineIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type { CostObjectTypeSymbol } from '@/app/types'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'

type ActionsProps = {
  costObjectId: number
  type: CostObjectTypeSymbol
}

const Actions: FC<ActionsProps> = ({ costObjectId, type }) => {
  const translate = useTranslation()
  const queryClient = useQueryClient()

  const [isSendEmailButtonDisabled, setIsSendEmailButtonDisabled] = useState(false)
  const [isSendAppSmsButtonDisabled, setIsSendAppSmsButtonDisabled] = useState(false)

  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const sendSmsMutation = useMutation(adminResources.sendCostObjectAppAwarenessMessage, {
    onSuccess() {
      setIsSendAppSmsButtonDisabled(true)

      setTimeout(() => {
        setIsSendAppSmsButtonDisabled(false)
      }, 5 * 1000)
    },
    onError() {
      setErrorPopupMessage({
        text: translate(
          "We couldn't send awareness message to this number. Please contact our support."
        ),
        title: translate('Awareness message mailing error'),
      })
    },
  })

  const sendEmailMutation = useMutation(adminResources.sendCostObjectAwarenessEmail, {
    onSuccess() {
      setIsSendEmailButtonDisabled(true)

      setTimeout(() => {
        setIsSendEmailButtonDisabled(false)
      }, 5 * 1000)
    },
    onError() {
      setErrorPopupMessage({
        text: translate(
          "We couldn't send awareness email to this number. Please contact our support."
        ),
        title: translate('Awareness message mailing error'),
      })
    },
  })

  const switchToCompanyNumberMutation = useMutation(adminResources.switchToCompanyNumber, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(costObjectId))
      queryClient.invalidateQueries(queries.adminCostObjectList(), { refetchType: 'all' })
    },
  })

  // TODO: Uncomment this when the feature is ready
  // const canTransferToCompanyNumber = type === 'E'
  const canTransferToCompanyNumber = false

  return (
    <>
      <div className="profile-actions">
        <h3>
          <Translation id="Actions" />
        </h3>

        <Button
          isIconRight
          className="mx-0"
          Icon={<MessageProcessingOutlineIcon />}
          disabled={isSendAppSmsButtonDisabled}
          onClick={() => sendSmsMutation.mutate(costObjectId)}
          isLoading={sendSmsMutation.isLoading}
        >
          <Translation id="Send mobile order sms" />
        </Button>

        <Button
          isIconRight
          className="mx-0"
          disabled={isSendEmailButtonDisabled}
          Icon={<EmailSendOutlineIcon />}
          onClick={() => sendEmailMutation.mutate(costObjectId)}
          isLoading={sendEmailMutation.isLoading}
        >
          <Translation id="Send awareness email" />
        </Button>

        {canTransferToCompanyNumber && (
          <Button
            className="mx-0"
            disabled={type !== 'E' || switchToCompanyNumberMutation.isLoading}
            onClick={() => switchToCompanyNumberMutation.mutate(costObjectId)}
            isLoading={switchToCompanyNumberMutation.isLoading}
          >
            <Translation id="Onboard private number into company" />
          </Button>
        )}
      </div>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />
    </>
  )
}

export default Actions
